import {
    IntensityMapRef,
    PropertySummary,
    TsneResult,
    UmapResult,
    clusteringAxisPropertyNames,
    clusteringResultMatchesLoadedMaps,
    dedupeClusteringResultsByName,
} from '../../state/case/case.state'

/** Intersection of property names across loaded maps (catalogs are sorted by name on load). */
export function computeSharedPropertyNames(
    propertyNamesByMapId: Record<number, string[]>,
    loadedMaps: { id: number }[]
): string[] {
    const nameLists = loadedMaps
        .map(m => propertyNamesByMapId[m.id])
        .filter((n): n is string[] => !!n && n.length > 0)
    if (nameLists.length === 0) return []
    // Single map: reuse the stored array reference (no copy).
    if (nameLists.length === 1) return nameLists[0]

    const otherNameSets = nameLists.slice(1).map(n => new Set(n))
    return nameLists[0].filter(name => otherNameSets.every(s => s.has(name)))
}

/** Same clustering visibility rules as filterCatalogByMatchedClusteringResults, for name lists. */
export function filterNamesByMatchedClusteringResults(
    names: string[],
    tsne: TsneResult[],
    umap: UmapResult[],
    loadedMaps: Iterable<IntensityMapRef>
): string[] {
    const allClusteringProps = clusteringAxisPropertyNames([...tsne, ...umap])
    if (allClusteringProps.size === 0) return names

    const matchedResults = dedupeClusteringResultsByName([
        ...tsne.filter(r => clusteringResultMatchesLoadedMaps(r, loadedMaps)),
        ...umap.filter(r => clusteringResultMatchesLoadedMaps(r, loadedMaps)),
    ])
    const allowedClusteringProps = clusteringAxisPropertyNames(matchedResults)
    return names.filter(
        name => !allClusteringProps.has(name) || allowedClusteringProps.has(name)
    )
}

/** Resolve a property summary from any loaded map's catalog (first hit wins). */
export function lookupPropertySummary(
    name: string,
    propertyCatalogByMapId: Record<number, PropertySummary[]>,
    loadedMaps: { id: number }[]
): PropertySummary | undefined {
    for (const map of loadedMaps) {
        const catalog = propertyCatalogByMapId[map.id]
        if (!catalog?.length) continue
        const found = catalog.find(p => p.name === name)
        if (found) return found
    }
    return undefined
}

/**
 * Build PropertySummary[] for shared names. Prefer avoiding this on hot paths;
 * use filterSharedPropertyOptions for dropdowns instead.
 */
export function resolvePropertySummaries(
    names: string[],
    propertyCatalogByMapId: Record<number, PropertySummary[]>,
    loadedMaps: { id: number }[]
): PropertySummary[] {
    if (names.length === 0 || loadedMaps.length === 0) return []

    let byName: Map<string, PropertySummary> | undefined
    for (const map of loadedMaps) {
        const catalog = propertyCatalogByMapId[map.id]
        if (catalog?.length) {
            byName = new Map(catalog.map(p => [p.name, p]))
            break
        }
    }
    if (!byName) return []

    const out: PropertySummary[] = []
    for (const name of names) {
        const summary = byName.get(name)
        if (summary) out.push(summary)
    }
    return out
}

export interface FilteredPropertyOptions {
    options: PropertySummary[]
    hasMore: boolean
}

/**
 * Search/shared-name preview for axis dropdowns: scan names, hydrate only up to `limit` summaries.
 */
export function filterSharedPropertyOptions(
    names: string[],
    search: string,
    limit: number,
    resolve: (name: string) => PropertySummary | undefined
): FilteredPropertyOptions {
    const trimmed = search.trim().toLowerCase()

    if (!trimmed) {
        const options: PropertySummary[] = []
        for (let i = 0; i < names.length && options.length < limit; i++) {
            const summary = resolve(names[i])
            if (summary) options.push(summary)
        }
        return { options, hasMore: names.length > limit }
    }

    const startsWithNames: string[] = []
    const containsNames: string[] = []
    for (const name of names) {
        const lower = name.toLowerCase()
        if (lower.startsWith(trimmed)) {
            startsWithNames.push(name)
        } else if (lower.includes(trimmed)) {
            containsNames.push(name)
        }
    }

    const matched =
        startsWithNames.length > 0 ? startsWithNames.concat(containsNames) : containsNames
    const options: PropertySummary[] = []
    for (let i = 0; i < matched.length && options.length < limit; i++) {
        const summary = resolve(matched[i])
        if (summary) options.push(summary)
    }
    return { options, hasMore: matched.length > limit }
}
